import { motion } from "framer-motion";
import { Plus, ExternalLink, Wifi } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import BankConnectionCard from "./BankConnectionCard";
import EmptyConnectionsState from "./EmptyConnectionsState";

// FIN-7 — BancosList
//
// Container das conexões. Header "● BANCOS X/Y" + CTA "+" + link externo.
// Empty state convidativo quando não há conexões.
//
// Props:
//   items[]           — lista de Connection
//   stats             — { total, updated, errored, updating }
//   enabled           — flag OPEN_FINANCE_ENABLED
//   onAddBank()       — handler do botão "+"
//   onRefresh(id)
//   onDisconnect(conn)
//   onOpenPluggyDash() — link externo pro dashboard do user na Pluggy

export default function BancosList({
  items = [],
  stats,
  enabled = false,
  onAddBank,
  onRefresh,
  onDisconnect,
  onOpenPluggyDash,
}) {
  const total = stats?.total ?? items.length;
  const ok = stats?.updated ?? items.filter((i) => i.status === "updated").length;
  const hasError = (stats?.errored ?? 0) > 0;

  const dotTone = hasError
    ? "bg-status-error shadow-[0_0_8px_var(--status-error)]"
    : ok > 0
      ? "bg-brand-green shadow-[0_0_8px_var(--brand-green)]"
      : "bg-foreground-muted";

  const addButton = (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={enabled ? onAddBank : undefined}
      disabled={!enabled}
      aria-label="Adicionar banco"
      data-testid="conexoes-add-bank"
      className={cn(
        "w-9 h-9 rounded-lg border-status-warning/50 bg-status-warning/10 text-status-warning hover:bg-status-warning/15 hover:text-status-warning",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    >
      <Plus className="w-4 h-4" aria-hidden="true" />
    </Button>
  );

  return (
    <Card
      variant="elevated"
      className="glass-card p-4 md:p-6"
      data-testid="conexoes-bancos-list"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className={cn("w-2 h-2 rounded-full", dotTone)} aria-hidden="true" />
          <h3 className="text-caption font-bold tracking-[0.18em] uppercase text-foreground-muted">
            Bancos
          </h3>
          <span className="px-2 py-0.5 rounded-md bg-status-warning/15 text-status-warning text-caption font-bold">
            {ok}/{total}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {!enabled ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>{addButton}</span>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-caption">
                  Disponível no pré-lançamento
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            addButton
          )}

          {onOpenPluggyDash && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onOpenPluggyDash}
              disabled={!enabled}
              aria-label="Abrir painel Pluggy"
              className="w-9 h-9 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* Lista ou empty */}
      {items.length === 0 ? (
        <EmptyConnectionsState enabled={enabled} onAddBank={onAddBank} />
      ) : (
        <motion.div
          layout
          className="space-y-3"
          data-testid="conexoes-bancos-items"
        >
          {items.map((conn) => (
            <BankConnectionCard
              key={conn.id}
              connection={conn}
              onRefresh={onRefresh}
              onDisconnect={onDisconnect}
              disabled={!enabled}
            />
          ))}
        </motion.div>
      )}
    </Card>
  );
}
